import { ExternalLink, Lock, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export interface RecommendedTool {
  name: string;
  path: string;
  reason: string;
  isPaid?: boolean;
  category?: string;
}

interface RecommendedToolsProps {
  tools: RecommendedTool[];
  onToolClick: (path: string) => void;
}

export const RecommendedTools = ({ tools, onToolClick }: RecommendedToolsProps) => {
  const navigate = useNavigate();

  if (tools.length === 0) {
    return null;
  }

  const handleToolClick = (tool: RecommendedTool) => {
    onToolClick(tool.path);
    navigate(tool.path);
  };

  return (
    <Card className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-primary/20">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Recommended Tools</CardTitle>
        </div>
        <CardDescription>
          Based on our conversation, these tools might help
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {tools.map((tool, index) => (
          <button
            key={index}
            onClick={() => handleToolClick(tool)}
            className="w-full text-left group relative overflow-hidden rounded-lg border border-border/50 bg-card/50 hover:bg-card transition-all hover:border-primary/50 hover:shadow-md cursor-pointer"
          >
            <div className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
                      {tool.name}
                    </h4>
                    {tool.isPaid ? (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Lock className="h-3 w-3" />
                        Premium
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Free
                      </Badge>
                    )}
                    {tool.category && (
                      <Badge variant="outline" className="text-xs">
                        {tool.category}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {tool.reason}
                  </p>
                </div>
                <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </div>
            
            {/* Gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
          </button>
        ))}
      </CardContent>
    </Card>
  );
};
